import { createSelector } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { NotificationStatus } from '../notifications';
import { RootState } from '../store';

const getConversationsState = (state: RootState) => state.conversations;

export const selectConversationPreviews = createSelector(
  [getConversationsState],
  (conversations) => {
    if (conversations.conversationPreviews.length > 0) {
      const conversationPreviews = conversations.conversationPreviews.map(
        (preview) => preview
      );
      return conversationPreviews.sort((a, b) =>
        dayjs(b.lastChatMessageDate).diff(dayjs(a.lastChatMessageDate))
      );
    } else return [];
  }
);

export const selectLoadingConversations = createSelector(
  [getConversationsState],
  (conversations) => conversations.loadingConversations
);

export const selectChatbox = createSelector(
  [getConversationsState],
  (conversations) => conversations.chatbox
);

export const selectMessagesContinuationToken = createSelector(
  [getConversationsState],
  (conversations) => conversations.chatbox.messages?.xMsContinuationToken
);

export const selectHasMoreMessages = createSelector(
  [getConversationsState],
  (conversations) =>
    Boolean(conversations.chatbox.messages?.xMsContinuationToken)
);

export const selectChatboxMessages = createSelector(
  [getConversationsState],
  (conversations) => [...(conversations.chatbox.messages?.data || [])]
);

export const selectLoadingMessages = createSelector(
  [getConversationsState],
  (conversations) => conversations.loadingMessages
);

export const selectNewConversations = createSelector(
  [
    getConversationsState,
    (state: RootState) =>
      state.profile.member?.id ?? state.profile.user?.memberId,
  ],
  (conversations, currentMemberId) => {
    if (!conversations.conversationPreviews.length) return false;

    return (
      conversations.conversationPreviews.find((conversation) => {
        // Prefer checking the unread status for the *current* member.
        if (currentMemberId) {
          if (conversation.toMemberId === currentMemberId) {
            return (
              conversation.lastChatMessageStatusToMember ===
              NotificationStatus.unread
            );
          }

          if (conversation.fromMemberId === currentMemberId) {
            return (
              conversation.lastChatMessageStatusFromMember ===
              NotificationStatus.unread
            );
          }
        }

        // Fallback when we can't confidently identify direction.
        return (
          conversation.lastChatMessageStatusFromMember ===
            NotificationStatus.unread ||
          conversation.lastChatMessageStatusToMember ===
            NotificationStatus.unread
        );
      }) ?? false
    );
  }
);

export const selectLoadingReadingConversation = createSelector(
  [getConversationsState],
  (conversations) => conversations.loadingReadingConversation
);

export const selectConversationDrawerOpen = createSelector(
  [getConversationsState],
  (conversations) => conversations.isConversationDrawerOpen
);

export const selectNewMessageFromCompanion = (memberId?: string) =>
  createSelector(
    [getConversationsState],
    (conversations) =>
      conversations.conversationPreviews.length > 0 &&
      conversations.conversationPreviews?.find(
        (conversation) =>
          conversation.fromMemberId === memberId &&
          conversation.lastChatMessageStatusToMember ===
            NotificationStatus.unread
      )
  );

export const selectChatId = (memberId?: string) =>
  createSelector([getConversationsState], (conversations) => {
    return conversations.conversationPreviews?.find(
      (conversation) => conversation.companionId === memberId
    )?.chatId;
  });

export const selectSearchInChatsDrawer = createSelector(
  [getConversationsState],
  (conversations) => {
    return conversations.searchInChatsDrawer;
  }
);

export const selectShowCompanionsInChatsDrawer = createSelector(
  [getConversationsState],
  (conversations) => conversations.showCompanionsInChatsDrawer
);
